import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, output, OutputEmitterRef, Signal } from '@angular/core';
import { ChipControlGroupComponent } from '@libs/form/components';
import { CapabilityService } from '@libs/shared/services';
import {
  OrderHistoryDelivery,
  OrderHistoryDeliveryOptions,
  type OrderHistoryFilter,
  OrderHistoryOrderDate,
  OrderHistoryShippingDate,
  OrderHistorySortBy,
  type OrderHistorySortByValueType,
} from '@libs/services/models';
import type { InputItemModel } from '@libs/form/models';
import { DateTime } from 'luxon';
import { isSameFilterDate } from '../order-history-filter.util';
import type { OrderHistoryFilterSortFormModel } from './order-history-filter-sort.model';

@Component({
  selector: 'ui-order-history-filter-sort',
  standalone: true,
  templateUrl: './order-history-filter-sort.component.html',
  styleUrls: ['./order-history-filter-sort.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChipControlGroupComponent,
  ],
})
export class OrderHistoryFilterSortComponent {
  public filter: InputSignal<OrderHistoryFilter> = input.required<OrderHistoryFilter>();
  public sort: InputSignal<OrderHistorySortByValueType | undefined> = input<OrderHistorySortByValueType | undefined>();

  public filterChange: OutputEmitterRef<OrderHistoryFilter> = output();
  public sortChange: OutputEmitterRef<OrderHistorySortByValueType | undefined> = output();

  public inputItems: Signal<InputItemModel[]> = computed(() => this.mapInputItems(this.filter(), this.sort()));

  private readonly capabilityService: CapabilityService = inject(CapabilityService);

  public handleValueChange(value: unknown): void {
    const parsedValue: OrderHistoryFilterSortFormModel = value as OrderHistoryFilterSortFormModel;

    const orderFrom: DateTime | undefined = parsedValue.orderDate?.start?.isValid ? parsedValue.orderDate.start : undefined;
    const orderTo: DateTime | undefined = parsedValue.orderDate?.end?.isValid ? parsedValue.orderDate.end : undefined;
    const shippingFrom: DateTime | undefined = parsedValue.shippingDate?.start?.isValid ? parsedValue.shippingDate.start : undefined;
    const shippingTo: DateTime | undefined = parsedValue.shippingDate?.end?.isValid ? parsedValue.shippingDate.end : undefined;

    const filterResult: OrderHistoryFilter = {
      order: { from: orderFrom, to: orderTo },
      shipping: { from: shippingFrom, to: shippingTo },
      delivery: parsedValue.delivery || undefined,
    };

    // the group emits the whole form on any change, so only emit the output that actually changed (e.g. changing the sort must not emit a filter).
    if (!this.isSameFilter(filterResult, this.filter())) {
      this.filterChange.emit(filterResult);
    }

    if (parsedValue.sort !== this.sort()) {
      this.sortChange.emit(parsedValue.sort);
    }
  }

  private isSameFilter(a: OrderHistoryFilter, b: OrderHistoryFilter): boolean {
    return isSameFilterDate(a.order?.from, b.order?.from)
      && isSameFilterDate(a.order?.to, b.order?.to)
      && isSameFilterDate(a.shipping?.from, b.shipping?.from)
      && isSameFilterDate(a.shipping?.to, b.shipping?.to)
      && a.delivery === b.delivery;
  }

  private mapInputItems(filter: OrderHistoryFilter, sort: OrderHistorySortByValueType | undefined): InputItemModel[] {
    const items: InputItemModel[] = [
      {
        ...OrderHistorySortBy,
        value: sort ?? '-create',
      },
      {
        ...OrderHistoryOrderDate,
        value: {
          start: filter.order?.from?.isValid ? filter.order.from : null,
          end: filter.order?.to?.isValid ? filter.order.to : null,
        },
      },
      {
        ...OrderHistoryShippingDate,
        value: {
          start: filter.shipping?.from?.isValid ? filter.shipping.from : null,
          end: filter.shipping?.to?.isValid ? filter.shipping.to : null,
        },
      },
    ];

    if (this.capabilityService.isAvailable('services.orderHistoryDeliveryFilter')()) {
      items.push({
        ...OrderHistoryDelivery,
        value: filter.delivery ?? '',
        options: OrderHistoryDeliveryOptions,
      });
    }

    return items;
  }
}
